import React, { useState, useEffect } from "react";
import {
  getEmailAvatarColor,
  getEmailInitials,
  getSenderDomain,
  getSenderLogoUrl,
  getRawSenderLogoUrl,
  getCachedLogoStatus,
  setCachedLogoStatus,
  MIN_SENDER_LOGO_PX,
} from "../helper/emailSenderLogo";
import "./EmailSenderAvatar.css";

/**
 * Circular sender avatar: brand logo when Google has one for the domain,
 * otherwise the coloured two-letter initials already used in the inbox.
 */
export default function EmailSenderAvatar({
  email,
  name,
  size = 30,
  className = "",
  style = {},
  children = null,
}) {
  const domain = getSenderDomain(email);
  const trimmedUrl = children ? null : getSenderLogoUrl(email);
  const rawUrl = children ? null : getRawSenderLogoUrl(email);
  const cached = domain ? getCachedLogoStatus(domain) : 'fail';
  const [logoFailed, setLogoFailed] = useState(cached === 'fail');
  const [logoReady, setLogoReady] = useState(cached === 'ok');
  // The trim proxy is what makes a padded icon fill the circle; if it is
  // unreachable we still show Google's untrimmed icon rather than initials.
  const [proxyFailed, setProxyFailed] = useState(false);
  const logoUrl = proxyFailed ? rawUrl : trimmedUrl;

  useEffect(() => {
    const next = domain ? getCachedLogoStatus(domain) : 'fail';
    setLogoFailed(!trimmedUrl || next === 'fail');
    setLogoReady(next === 'ok');
    setProxyFailed(false);
  }, [domain, trimmedUrl]);

  const label = name || email || "";
  const initials = getEmailInitials(name || email);
  const bg = getEmailAvatarColor(label);
  const showLogo = !children && logoUrl && !logoFailed;
  // Favicons are often transparent; keep initials only as a loading
  // placeholder so they never bleed through the brand icon.
  const logoVisible = showLogo && logoReady;

  const markFail = () => {
    if (!proxyFailed && rawUrl) {
      setProxyFailed(true);
      return;
    }
    if (domain) setCachedLogoStatus(domain, false);
    setLogoFailed(true);
    setLogoReady(false);
  };

  const handleLoad = (e) => {
    const img = e.currentTarget;
    const w = img.naturalWidth || 0;
    if (w > 0 && w < MIN_SENDER_LOGO_PX) {
      markFail();
      return;
    }
    if (domain) setCachedLogoStatus(domain, true);
    setLogoReady(true);
  };

  return (
    <div
      className={`user-avatar-img loaded email-sender-avatar${logoVisible ? " email-sender-avatar--logo" : ""} ${className}`.trim()}
      aria-hidden
      style={{
        "--email-sender-avatar-size": `${size}px`,
        "--email-sender-avatar-fs": `${Math.round(size * 0.38)}px`,
        "--email-sender-avatar-bg": bg,
        ...style,
      }}
    >
      {logoVisible ? null : (children || initials)}
      {showLogo ? (
        <>
          {/* Safety net for the untrimmed fallback icon: a blown-up, blurred copy
              paints the logo's own colours edge to edge, so a mark sitting on a
              transparent canvas still reads as a filled circle. */}
          <img
            key={`${logoUrl}-bleed`}
            src={logoUrl}
            alt=""
            aria-hidden
            className="email-sender-logo-bleed email-sender-avatar__bleed"
            referrerPolicy="no-referrer"
            decoding="async"
            style={{ opacity: logoReady ? 1 : 0 }}
          />
          <img
            key={logoUrl}
            src={logoUrl}
            alt=""
            width={size}
            height={size}
            className="user-avatar-img loaded email-sender-avatar__logo"
            referrerPolicy="no-referrer"
            decoding="async"
            onError={markFail}
            onLoad={handleLoad}
            style={{ opacity: logoReady ? 1 : 0 }}
          />
        </>
      ) : null}
    </div>
  );
}
